import { UserRole, type Assignment } from '@teentack/shared';
import { useAuth } from '@/auth/use-auth';
import { usePosterAssignments } from '@/features/assignments/use-assignments';
import {
  useConfirmWardCompletion,
  useWardAssignments,
} from '@/features/parent/use-parent-overview';
import { WorksitePanel } from '@/features/worksite/WorksitePanel';
import { AssignmentActions } from '@/features/assignments/AssignmentActions';
import { FavouriteButton } from '@/features/favourites/FavouriteButton';
import { EmptyState } from '@/components/ui/Feedback';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { MapPinIcon } from '@/components/ui/Icons';
import { ErrorNote } from '@/components/Form';
import { presentApiError } from '@/lib/form-errors';

/**
 * Work under way. [Parent steps 14-16, Job Poster steps 23-24]
 *
 * The read-only half of the worksite panel, gathered onto one page so a
 * guardian or an employer has somewhere to look while a job is happening
 * rather than hunting through a list of jobs.
 */

/**
 * A guardian confirming their teen's finished work. [Parent step 18]
 *
 * Deliberately not framed as approval: the employer approves the work and
 * releases the payment. This says the family agrees it was done as described,
 * which is what the employer sees alongside it when they decide. A guardian who
 * could block a release would have leverage over an employer that the product
 * should not hand them.
 */
function GuardianSignOff({ assignment }: { assignment: Assignment }) {
  const confirm = useConfirmWardCompletion();

  if (assignment.guardianConfirmedAt) {
    return (
      <div className="rounded-xl border border-safe-200 bg-safe-50 px-4 py-3">
        <p className="text-sm font-semibold text-safe-700">You confirmed this work</p>
        <p className="mt-0.5 text-xs leading-relaxed text-safe-700/80">
          The employer has been told. Approving the work and releasing the payment is still theirs
          to do.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-line bg-white px-4 py-3">
      <p className="text-sm font-semibold text-ink">Did this go as described?</p>
      <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
        Your confirmation goes on the record and is shown to the employer. It does not release the
        payment — that stays with them.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button size="sm" disabled={confirm.isPending} onClick={() => confirm.mutate(assignment.id)}>
          {confirm.isPending ? 'Confirming…' : 'Confirm the work'}
        </Button>
        {confirm.isError && (
          <span className="text-xs font-medium text-red-600">
            {presentApiError(confirm.error, 'Could not confirm that').message}
          </span>
        )}
      </div>
    </div>
  );
}

function AssignmentCard({
  assignment,
  showActions,
}: {
  assignment: Assignment;
  /** Only the employer can approve and pay; a guardian is here to watch. */
  showActions: boolean;
}) {
  return (
    <li className="rounded-2xl border border-line bg-canvas p-5 shadow-soft">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-bold text-ink">{assignment.job.title}</h2>
          <p className="mt-0.5 text-xs text-slate-500">{assignment.teen.name}</p>
        </div>
        <Badge tone={assignment.status === 'COMPLETED' ? 'accent' : 'brand'}>
          {assignment.status.charAt(0) + assignment.status.slice(1).toLowerCase().replace('_', ' ')}
        </Badge>
      </div>

      {showActions && (
        <div className="mt-4">
          <AssignmentActions assignment={assignment} />
        </div>
      )}

      {/* The guardian's half of finishing a job. [Parent step 18] */}
      {!showActions && assignment.status === 'COMPLETED' && (
        <div className="mt-4">
          <GuardianSignOff assignment={assignment} />
        </div>
      )}

      {/* The moment an employer knows whether they would hire this teen again
          is the moment the work finishes, so the star lives here too. */}
      {showActions && (
        <div className="mt-3">
          <FavouriteButton teenId={assignment.teen.id} teenName={assignment.teen.name} />
        </div>
      )}

      <div className="mt-4">
        <WorksitePanel assignmentId={assignment.id} canPost={false} />
      </div>
    </li>
  );
}

export function TrackingPage() {
  const { user } = useAuth();
  const isPoster = user?.role === UserRole.JOB_POSTER;

  const isParent = user?.role === UserRole.PARENT;

  // Each role reaches the same assignments by a different route: an employer
  // through the jobs they own, a guardian through the teens they approve for.
  const posterAssignments = usePosterAssignments({ pageSize: 50 }, isPoster);
  const wardAssignments = useWardAssignments({ pageSize: 50 }, isParent);

  const source = isPoster ? posterAssignments : wardAssignments;

  // An approved-but-unpaid job still needs the employer, so it stays on this
  // page until the money is actually released rather than vanishing at sign-off.
  const items = (source.data?.items ?? []).filter(
    (assignment) =>
      ['ACCEPTED', 'IN_PROGRESS', 'COMPLETED'].includes(assignment.status) ||
      (assignment.status === 'APPROVED' && assignment.paymentStatus !== 'RELEASED'),
  );

  const error = source.error;

  return (
    <main className="container-page py-10">
      <header className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
          Right now
        </p>
        <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
          Work under way
        </h1>
        <p className="mt-2 max-w-prose text-sm leading-relaxed text-slate-500">
          {isPoster
            ? 'Sign off finished work, release payment, and follow progress as it is posted.'
            : 'Progress updates as they are posted, and a location when the teen chooses to share one.'}{' '}
          Nobody is tracked without turning it on themselves.
        </p>
      </header>

      {error && (
        <ErrorNote message={presentApiError(error, 'Could not load work under way').message} />
      )}

      {source.isPending && (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-40" />
        </div>
      )}

      {source.data && items.length === 0 && (
        <EmptyState icon={<MapPinIcon className="size-6" />} title="Nothing under way">
          {isPoster
            ? 'Once a teen accepts one of your jobs, their updates appear here.'
            : 'Once your teen accepts a job, their updates appear here.'}
        </EmptyState>
      )}

      {items.length > 0 && (
        <ul className="space-y-5">
          {items.map((assignment) => (
            <AssignmentCard
              key={assignment.id}
              assignment={assignment}
              showActions={isPoster}
            />
          ))}
        </ul>
      )}
    </main>
  );
}
